import React from 'react';
import { TagColored } from '@spark';
import { TaskType, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import SampleTextBlockCard from '@/features/tasks/components/SampleTextBlockCard';
import TaggedSample from '@/features/evaluation/components/TaggedSample';

// Best-effort text extractor for summarization ground truth, which the
// backend stores either as a plain string or as a GenerationResponse-shaped
// object ({ response, extra_params, is_validated }). Mirrors the logic that
// previously lived inside SummarizationSampleDetail so both surfaces decode
// the same input the same way.
const extractSummaryText = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && typeof v?.response === 'string') return v.response;
    try {
        return JSON.stringify(v, null, 2);
    } catch (e) {
        return String(v);
    }
};

// Predicate exported alongside the card so callers can decide whether to
// even show the "View Reference Response" toggle. Mirrors the exact null
// conditions used inside the component below — no card means no toggle.
const GENERATIVE_TASK_TYPES_LOWER = [
    TaskType.summarization.toLowerCase(),
    TaskType.contentGeneration.toLowerCase(),
];

export const hasReferenceContent = ({ taskType, groundTruth, requestExample }) => {
    const tt = (taskType || '').toLowerCase();
    if (GENERATIVE_TASK_TYPES_LOWER.includes(tt)) {
        return extractSummaryText(groundTruth).trim().length > 0;
    }
    if (
        tt === TaskType.NER.toLowerCase() ||
        tt === TaskType.NER_WITH_RELATIONS.toLowerCase()
    ) {
        return Array.isArray(groundTruth?.entities) && !!requestExample;
    }
    if (tt === TaskType.classification.toLowerCase()) {
        return Array.isArray(groundTruth?.labels) && groundTruth.labels.length > 0;
    }
    return false;
};

const COMMON_SUBTITLES = {
    ner: 'Ground truth entities highlighted on the example',
    classification: 'Ground truth labels for this example',
};

// Single source of truth for the "Reference Response" panel. Used by:
//   - the Evaluation > Summarization sample detail (replaces the inline
//     "Reference Summary" card so both surfaces stay in lock-step), and
//   - the Playground detail view's third column when the user toggles
//     "View Reference Response".
//
// Returns ``null`` when there is no meaningful ground truth payload for the
// task type (handcrafted Playground examples, summarization with no test
// response, classification with no labels). Callers use this signal to also
// hide their toggle button so the user is never offered an empty card.
const ReferenceResponseCard = ({
    taskType,
    groundTruth,
    requestExample,
    labelDisplayNames,
    title = 'Reference Response',
    maxHeight = 360,
}) => {
    const tt = (taskType || '').toLowerCase();

    if (GENERATIVE_TASK_TYPES_LOWER.includes(tt)) {
        const text = extractSummaryText(groundTruth);
        if (!text) return null;
        const noun =
            tt === TaskType.contentGeneration.toLowerCase()
                ? getGenerativeOutputNoun(TaskType.contentGeneration)
                : getGenerativeOutputNoun(TaskType.summarization);
        return (
            <SampleTextBlockCard
                title={title}
                subtitle={`Ground truth ${noun} (test response)`}
                text={text}
                maxHeight={maxHeight}
            />
        );
    }

    if (
        tt === TaskType.NER.toLowerCase() ||
        tt === TaskType.NER_WITH_RELATIONS.toLowerCase()
    ) {
        const entities = groundTruth?.entities;
        if (!Array.isArray(entities) || !requestExample) return null;
        const allLabels = Object.keys(labelDisplayNames || {});
        return (
            <SampleTextBlockCard
                title={title}
                subtitle={COMMON_SUBTITLES.ner}
                metaText={requestExample}
                maxHeight={maxHeight}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                    }}
                >
                    <TaggedSample
                        isGroundTruth
                        text={requestExample}
                        entities={entities}
                        allLabels={allLabels}
                        disableCard
                    />
                </div>
            </SampleTextBlockCard>
        );
    }

    if (tt === TaskType.classification.toLowerCase()) {
        const labels = groundTruth?.labels;
        if (!Array.isArray(labels) || labels.length === 0) return null;
        const allLabels = Object.keys(labelDisplayNames || {});
        return (
            <SampleTextBlockCard
                title={title}
                subtitle={COMMON_SUBTITLES.classification}
                metaText={labels.join(', ')}
                maxHeight={maxHeight}
            >
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        alignItems: 'center',
                    }}
                >
                    {labels.map((label, idx) => (
                        <TagColored
                            key={`${label}-${idx}`}
                            label={label}
                            variant={getSparkTagVariant(label, allLabels)}
                        />
                    ))}
                </div>
            </SampleTextBlockCard>
        );
    }

    return null;
};

export default ReferenceResponseCard;
